import React, { useState, useEffect } from "react";
import { useRecoilState } from "recoil";
import { settingBackPathState, userInfoState } from "../global-state";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { useTheme } from "@mui/material/styles";
import LocationDialog from "./location-dialog";
import {
  deleteLocation,
  listLocations,
  saveLocation,
} from "./location-service";
import { useToast } from "../common/toast";
import useApiCall from "../common/api-call";
import { useHistory } from "react-router-dom";

const Location = () => {
  const theme = useTheme();
  const [userInfo] = useRecoilState(userInfoState);
  const [locations, setLocations] = useState([]);
  const [locationNames, setLocationNames] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [settingBackPath, setSettingBackPath] =
    useRecoilState(settingBackPathState);
  const { successToast, warningToast, errorToast } = useToast();
  const history = useHistory();
  const { apiCall } = useApiCall();

  const isSmallScreen = useMediaQuery("(max-width:768px)");

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    const resp = await listLocations(apiCall, userInfo.activeScope.id);
    if (resp) {
      setLocations(resp.locations);
      setLocationNames(resp.locations.map((location) => location.name));
    } else {
      errorToast("Error!");
    }
  };

  const handleAddLocation = () => {
    setEditLocation(null);
    setDialogOpen(true);
  };

  const handleEditLocation = (location) => {
    setEditLocation(location);
    setDialogOpen(true);
  };

  const handleSaveLocation = async (locationData) => {
    locationData.scopeId = userInfo.activeScope.id;
    console.log(locationData);
    const resp = await saveLocation(apiCall, locationData);
    if (resp) {
      successToast("Saved");
      fetchLocations();
    } else {
      errorToast("Error!");
    }
  };

  const handleDelete = async (locationData) => {
    locationData.scopeId = userInfo.activeScope.id;
    const resp = await deleteLocation(apiCall, locationData);
    if (resp) {
      successToast("Deleted");
      fetchLocations();
    } else {
      errorToast("Error!");
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="start"
      alignItems="start"
      flexDirection="column"
      height="100%"
      p={2}
      width="100%"
    >
      <Box
        position="static"
        sx={{
          display: "flex",
          alignItems: "center",
          justfyContent: "flex-start",
          mb: 2,
        }}
      >
        {isSmallScreen && (
          <IconButton
            color="inherit"
            edge="start"
            sx={{ mr: 1 }}
            onClick={() => {
              if (settingBackPath) {
                history.replace(settingBackPath);
                setSettingBackPath("");
              } else {
                history.replace("/setting");
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ display: "flex" }}>
          <Typography variant="h6" style={{ textAlign: "center" }}>
            Locations
          </Typography>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap={1} width="100%">
        <Card
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: "8px",
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  Space Locations
                </Typography>
                <Tooltip title="Add new location">
                  <IconButton onClick={handleAddLocation} color="primary">
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <List>
                {locations.map((location) => (
                  <ListItem
                    key={location.id}
                    sx={{
                      mb: 1,
                      backgroundColor: "background.card",
                      borderRadius: "4px",
                    }}
                  >
                    <ListItemText
                      primary={location.name}
                      secondary={location.address}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Edit location">
                        <IconButton
                          edge="end"
                          onClick={() => handleEditLocation(location)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          </CardContent>
        </Card>

        <Card
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: "8px",
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  color: theme.palette.primary.main,
                  mr: 2,
                }}
              />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  How Locations Are Used
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Location data is utilized solely to enable various
                  integrations, such as Time and Weather.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <LocationDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditLocation(null);
        }}
        onSave={handleSaveLocation}
        onDelete={handleDelete}
        initialData={editLocation}
        locationNames={locationNames}
      />
    </Box>
  );
};

export default Location;
